import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const getStatusColor = (status) => {
  switch (status) {
    case 'upcoming': return '#C9A227';
    case 'ongoing': return '#E85D4C';
    case 'completed': return '#6B8F71';
    case 'cancelled': return '#EF4444';
    default: return '#6B7280';
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (dateString) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const EventHero = ({ event, ctaLabel = 'View Details' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const status = event?.status || 'upcoming';

  useEffect(() => {
    if (!event?.start_date) return undefined;

    const tick = () => {
      const distance = new Date(event.start_date).getTime() - Date.now();
      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return false;
      }

      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
      return true;
    };

    if (!tick()) return undefined;

    const timer = setInterval(() => {
      if (!tick()) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [event]);

  if (!event) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '100vh', sm: '80vh', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        backgroundImage: event.image_url
          ? `url(${resolveMediaUrl(event.image_url)})`
          : 'url(/theme/oc-lan.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(20, 17, 14, 0.9) 0%, rgba(36, 30, 24, 0.88) 100%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', color: 'white', py: { xs: 4, sm: 0 } }}>
          <Chip
            label={status.toUpperCase()}
            sx={{
              mb: { xs: 2, sm: 3 },
              bgcolor: alpha(getStatusColor(status), 0.2),
              color: getStatusColor(status),
              border: `1px solid ${alpha(getStatusColor(status), 0.3)}`,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 700,
              py: { xs: 2, sm: 2.5 },
              px: { xs: 1, sm: 2 },
            }}
          />

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '4rem', md: '6rem' },
              fontWeight: 800,
              mb: { xs: 2, sm: 3 },
              color: '#F3EDE2', fontFamily: '"Fraunces", serif',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {event.name}
          </Typography>

          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 600,
                color: '#C9A227',
                fontFamily: '"Barlow Condensed", sans-serif',
                mb: 1,
              }}
            >
              {formatDate(event.start_date)}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                color: alpha('#fff', 0.8),
              }}
            >
              {formatTime(event.start_date)}
            </Typography>
          </Box>

          {status === 'upcoming' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 2, sm: 3 },
                mb: { xs: 4, sm: 5 },
                flexWrap: 'wrap',
              }}
            >
              {[
                { value: countdown.days, label: 'Days' },
                { value: countdown.hours, label: 'Hours' },
                { value: countdown.minutes, label: 'Min' },
                { value: countdown.seconds, label: 'Sec' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    minWidth: { xs: 70, sm: 100 },
                    bgcolor: alpha('#241E18', 0.85),
                    border: '1px solid rgba(201, 162, 39, 0.45)',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '2rem', sm: '3rem' },
                      fontWeight: 700,
                      color: '#C9A227',
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: alpha('#fff', 0.7),
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowIcon />}
            fullWidth={isMobile}
            onClick={() => navigate(`/events/${event.id || event._id}`)}
            sx={{
              px: { xs: 4, sm: 6 },
              py: { xs: 2, sm: 2.5 },
              fontSize: { xs: '1rem', sm: '1.2rem' },
              backgroundColor: '#E85D4C',
              color: '#14110E',
              fontWeight: 700,
              border: '1px solid rgba(201, 162, 39, 0.3)',
              maxWidth: { xs: '100%', sm: '400px' },
              '&:hover': {
                backgroundColor: '#C44738',
              },
            }}
          >
            {ctaLabel}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default EventHero;
